import React, { useState, useEffect } from 'react';
import { Plus, Search } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import DataTable from '../../components/admin/DataTable';
import ImageUpload from '../../components/admin/ImageUpload';
import RichTextEditor from '../../components/admin/RichTextEditor';
import { getImageUrl } from '../../lib/utils';

const NewsManagement = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    title_ar: '',
    title_en: '',
    content_ar: '',
    content_en: '',
    excerpt_ar: '',
    excerpt_en: '',
    image: '',
    images: [],
    video_url: '',
    links: [],
    category: 'أخبار',
    is_published: true,
  });
  const [newLink, setNewLink] = useState({ title: '', url: '' });
  const [newImageUrl, setNewImageUrl] = useState('');

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/news/all`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();
      setNews(data);
    } catch (error) {
      console.error('Error fetching news:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const url = editingNews
        ? `${BACKEND_URL}/api/news/${editingNews.id}`
        : `${BACKEND_URL}/api/news/`;

      const response = await fetch(url, {
        method: editingNews ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        fetchNews();
        resetForm();
        alert(editingNews ? 'تم تحديث الخبر بنجاح' : 'تم إضافة الخبر بنجاح');
      }
    } catch (error) {
      console.error('Error saving news:', error);
      alert('حدث خطأ أثناء حفظ الخبر');
    }
  };

  const handleEdit = (newsItem) => {
    setEditingNews(newsItem);
    setFormData({
      title_ar: newsItem.title_ar || '',
      title_en: newsItem.title_en || '',
      content_ar: newsItem.content_ar || '',
      content_en: newsItem.content_en || '',
      excerpt_ar: newsItem.excerpt_ar || '',
      excerpt_en: newsItem.excerpt_en || '',
      image: newsItem.image || '',
      images: newsItem.images || [],
      video_url: newsItem.video_url || '',
      links: newsItem.links || [],
      category: newsItem.category || 'أخبار',
      is_published: newsItem.is_published !== undefined ? newsItem.is_published : true,
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      title_ar: '',
      title_en: '',
      content_ar: '',
      content_en: '',
      excerpt_ar: '',
      excerpt_en: '',
      image: '',
      images: [],
      video_url: '',
      links: [],
      category: 'أخبار',
      is_published: true,
    });
    setEditingNews(null);
    setShowForm(false);
    setNewLink({ title: '', url: '' });
    setNewImageUrl('');
  };

  const addLink = () => {
    if (newLink.title && newLink.url) {
      setFormData({
        ...formData,
        links: [...formData.links, { ...newLink }]
      });
      setNewLink({ title: '', url: '' });
    }
  };

  const removeLink = (index) => {
    setFormData({
      ...formData,
      links: formData.links.filter((_, i) => i !== index)
    });
  };

  const addImage = () => {
    if (newImageUrl) {
      setFormData({
        ...formData,
        images: [...formData.images, newImageUrl]
      });
      setNewImageUrl('');
    }
  };

  const removeImage = (index) => {
    setFormData({
      ...formData,
      images: formData.images.filter((_, i) => i !== index)
    });
  };

  const handleDelete = async (newsItem) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/news/${newsItem.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        fetchNews();
        alert('تم حذف الخبر بنجاح');
      }
    } catch (error) {
      console.error('Error deleting news:', error);
      alert('حدث خطأ أثناء حذف الخبر');
    }
  };

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const filteredNews = news.filter(item =>
    item.title_ar?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.title_en?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredNews.length / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, filteredNews.length);
  const paginatedNews = filteredNews.slice(startIndex, endIndex);

  const columns = [
    {
      header: 'الصورة',
      accessor: 'image',
      render: (row) => (
        <img
          src={getImageUrl(row.image, 'https://via.placeholder.com/60')}
          alt={row.title_ar}
          className="w-16 h-16 object-cover rounded"
        />
      ),
    },
    { header: 'العنوان (عربي)', accessor: 'title_ar' },
    { header: 'العنوان (English)', accessor: 'title_en' },
    { header: 'التصنيف', accessor: 'category' },
    {
      header: 'الحالة',
      accessor: 'is_published',
      render: (row) => (
        <span className={`px-2 py-1 rounded text-xs ${row.is_published ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
          {row.is_published ? 'منشور' : 'مسودة'}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-[#0F388E]">إدارة الأخبار والفعاليات</h1>
          <p className="text-gray-600 mt-1">إضافة وتعديل أخبار الجامعة</p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setShowForm(!showForm);
          }}
          className="bg-[#0F388E] hover:bg-[#0F388E]/90"
        >
          <Plus className="ml-2 w-5 h-5" />
          إضافة خبر جديد
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>{editingNews ? 'تعديل الخبر' : 'إضافة خبر جديد'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Arabic Section */}
              <div className="bg-blue-50 p-4 rounded-lg space-y-4">
                <h3 className="font-bold text-[#0F388E] mb-3">المحتوى العربي</h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    العنوان بالعربية *
                  </label>
                  <input
                    type="text"
                    value={formData.title_ar}
                    onChange={(e) => setFormData({ ...formData, title_ar: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                    required
                    dir="rtl"
                  />
                </div>
                <RichTextEditor
                  label="المقتطف بالعربية"
                  value={formData.excerpt_ar}
                  onChange={(value) => setFormData({ ...formData, excerpt_ar: value })}
                  placeholder="مقتطف قصير عن الخبر..."
                  rows={3}
                />
                <RichTextEditor
                  label="المحتوى بالعربية *"
                  value={formData.content_ar}
                  onChange={(value) => setFormData({ ...formData, content_ar: value })}
                  placeholder="المحتوى الكامل للخبر..."
                  rows={8}
                />
              </div>

              {/* English Section */}
              <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                <h3 className="font-bold text-[#0F388E] mb-3">English Content</h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title in English
                  </label>
                  <input
                    type="text"
                    value={formData.title_en}
                    onChange={(e) => setFormData({ ...formData, title_en: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                    dir="ltr"
                  />
                </div>
                <RichTextEditor
                  label="Excerpt in English"
                  value={formData.excerpt_en}
                  onChange={(value) => setFormData({ ...formData, excerpt_en: value })}
                  placeholder="Short excerpt about the news..."
                  rows={3}
                />
                <RichTextEditor
                  label="Content in English"
                  value={formData.content_en}
                  onChange={(value) => setFormData({ ...formData, content_en: value })}
                  placeholder="Full news content..."
                  rows={8}
                />
              </div>

              {/* Gallery Images */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-lg border border-purple-100 space-y-4">
                <h3 className="font-bold text-[#0F388E] text-lg">معرض الصور</h3>
                <p className="text-sm text-gray-600">أضف صوراً إضافية تظهر في معرض الصور بصفحة التفاصيل (يمكنك الرفع المباشر أو كتابة الرابط)</p>
                
                <div className="space-y-3">
                  <ImageUpload
                    label="رفع صورة إلى المعرض من جهازك"
                    value=""
                    onChange={(uploadedPath) => {
                      if (uploadedPath) {
                        setFormData(prev => ({
                          ...prev,
                          images: [...(prev.images || []), uploadedPath]
                        }));
                      }
                    }}
                    category="news"
                  />

                  <div className="flex gap-2 items-center pt-2">
                    <span className="text-xs text-gray-500 whitespace-nowrap">أو رابط خارجي:</span>
                    <input
                      type="url"
                      value={newImageUrl}
                      onChange={(e) => setNewImageUrl(e.target.value)}
                      placeholder="رابط الصورة (https://...)"
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E] text-sm"
                    />
                    <Button type="button" onClick={addImage} className="bg-[#C9A040]">
                      <Plus className="w-4 h-4 ml-1" />
                      إضافة الرابط
                    </Button>
                  </div>
                </div>

                {formData.images && formData.images.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                    {formData.images.map((img, index) => (
                      <div key={index} className="relative group border rounded-lg overflow-hidden shadow-sm">
                        <img 
                          src={getImageUrl(img)} 
                          alt={`صورة ${index + 1}`} 
                          className="w-full h-24 object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Video URL */}
              <div className="bg-gradient-to-r from-red-50 to-orange-50 p-6 rounded-lg border border-red-100 space-y-4">
                <h3 className="font-bold text-[#0F388E] text-lg">الفيديو</h3>
                <p className="text-sm text-gray-600">أضف رابط فيديو YouTube أو رابط فيديو مباشر</p>
                <input
                  type="url"
                  value={formData.video_url}
                  onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                  placeholder="https://youtube.com/watch?v=... أو رابط فيديو مباشر"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                />
              </div>

              {/* Related Links */}
              <div className="bg-gradient-to-r from-cyan-50 to-blue-50 p-6 rounded-lg border border-cyan-100 space-y-4">
                <h3 className="font-bold text-[#0F388E] text-lg">الروابط ذات الصلة</h3>
                <p className="text-sm text-gray-600">أضف روابط مفيدة ذات صلة بالخبر</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={newLink.title}
                    onChange={(e) => setNewLink({ ...newLink, title: e.target.value })}
                    placeholder="عنوان الرابط"
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                  />
                  <div className="flex gap-2">
                    <input
                      type="url"
                      value={newLink.url}
                      onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                      placeholder="https://..."
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                    />
                    <Button type="button" onClick={addLink} className="bg-[#C9A040]">
                      <Plus className="w-4 h-4 ml-1" />
                      إضافة
                    </Button>
                  </div>
                </div>

                {formData.links && formData.links.length > 0 && (
                  <div className="space-y-2 mt-4">
                    {formData.links.map((link, index) => (
                      <div key={index} className="flex items-center justify-between bg-white p-3 rounded-lg border">
                        <div className="flex-1">
                          <div className="font-medium text-sm">{link.title}</div>
                          <div className="text-xs text-gray-500 truncate">{link.url}</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeLink(index)}
                          className="text-red-500 hover:text-red-700 px-2"
                        >
                          حذف
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {/* Main Image */}
              <div className="bg-green-50 p-6 rounded-lg border border-green-100">
                <h3 className="font-bold text-[#0F388E] text-lg mb-4">الصورة الرئيسية</h3>
                <ImageUpload
                  label="صورة الخبر"
                  value={formData.image}
                  onChange={(value) => setFormData({ ...formData, image: value })}
                  category="news"
                />
              </div>

              {/* Settings Section */}
              <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                <h3 className="font-bold text-[#0F388E] mb-3">الإعدادات</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      التصنيف
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
                    >
                      <option value="أخبار">أخبار</option>
                      <option value="فعاليات">فعاليات</option>
                      <option value="إنجازات">إنجازات</option>
                      <option value="إعلانات">إعلانات</option>
                    </select>
                  </div>

                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="is_published"
                      checked={formData.is_published}
                      onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                      className="w-5 h-5 text-[#0F388E] rounded focus:ring-[#0F388E]"
                    />
                    <label htmlFor="is_published" className="text-sm font-medium text-gray-700">
                      نشر الخبر مباشرة
                    </label>
                  </div>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex gap-3 justify-end pt-4 border-t">
                <Button type="button" variant="outline" onClick={resetForm}>
                  إلغاء
                </Button>
                <Button type="submit" className="bg-[#0F388E] hover:bg-[#0F388E]/90">
                  {editingNews ? 'تحديث' : 'إضافة'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-2">
            <Search className="w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="بحث في الأخبار..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F388E]"
            />
          </div>
        </CardContent>
      </Card>

      {/* News Table with Pagination */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center justify-between mb-4 pb-3 border-b text-sm text-gray-600">
            <span className="font-semibold text-[#0F388E]">
              إجمالي الأخبار: {filteredNews.length} خبراً
            </span>
            <span className="bg-blue-50 text-[#0F388E] px-3 py-1 rounded-full text-xs font-medium">
              عرض {filteredNews.length > 0 ? startIndex + 1 : 0} - {endIndex} (صفحة {currentPage} من {totalPages})
            </span>
          </div>

          <DataTable
            columns={columns}
            data={paginatedNews}
            onEdit={handleEdit}
            onDelete={handleDelete}
            loading={loading}
          />

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pt-4 border-t border-gray-100">
              <span className="text-xs text-gray-500">
                10 أخبار في كل صفحة
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  السابق
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                    .map((p, idx, arr) => {
                      const prev = arr[idx - 1];
                      return (
                        <React.Fragment key={p}>
                          {prev && p - prev > 1 && <span className="px-1 text-gray-400">...</span>}
                          <Button
                            variant={currentPage === p ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setCurrentPage(p)}
                            className={currentPage === p ? 'bg-[#0F388E] text-white' : ''}
                          >
                            {p}
                          </Button>
                        </React.Fragment>
                      );
                    })}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  التالي
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default NewsManagement;
